import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui";
import api from "../lib/api";
import { LayoutDashboard, Bell, Users, Globe, Sun, Moon, ShieldCheck } from "lucide-react";

const ENTITIES = [
  { name: "Holding", color: "#2563EB" },
  { name: "Consulting", color: "#10B981" },
  { name: "Ecommerce", color: "#EA580C" },
  { name: "SCI", color: "#F59E0B" },
];

function postAuthDest() {
  const token = localStorage.getItem("pending_invite_token");
  return token ? `/accept-invite?token=${encodeURIComponent(token)}` : "/";
}

export default function Login() {
  const { t, user, authLoading, toggleLang, lang, toggleTheme, theme } = useApp();
  const navigate = useNavigate();
  const [google, setGoogle] = useState({ enabled: false, client_id: "" });
  const tr = (fr, en) => (lang === "fr" ? fr : en);

  useEffect(() => {
    if (!authLoading && user) navigate(postAuthDest(), { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    api.get("/auth/google/config").then(({ data }) => setGoogle(data)).catch(() => {});
  }, []);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const params = new URLSearchParams({
      client_id: google.client_id,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "offline",
      include_granted_scopes: "true",
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const features = [
    { icon: LayoutDashboard, text: t("login_features_1") },
    { icon: Bell, text: t("login_features_2") },
    { icon: Users, text: t("login_features_3") },
  ];

  return (
    <div className="h-screen w-full grid lg:grid-cols-2 bg-background overflow-hidden">
      {/* Left — brand / pitch */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r"
        style={{ background: "linear-gradient(160deg, #18181b 0%, #09090b 60%)" }}>
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="login-logo-home">
          <div className="w-9 h-9 rounded-xl bg-white text-black flex items-center justify-center font-heading font-black text-lg transition-transform group-hover:scale-105">
            CO
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-white">{t("app_name")}</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">
              {t("app_tagline")}
            </div>
          </div>
        </Link>

        <div className="max-w-md">
          <h1 className="text-4xl xl:text-5xl font-heading font-medium tracking-tight leading-[1.05] text-white mb-5">
            {t("login_title")}
          </h1>
          <p className="text-white/70 text-base leading-relaxed mb-10">{t("login_subtitle")}</p>

          <div className="flex flex-wrap gap-2 mb-10">
            {ENTITIES.map((e) => (
              <span key={e.name} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 text-sm text-white bg-white/5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />
                {e.name}
              </span>
            ))}
          </div>

          <div className="space-y-3">
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-white/70">
                <f.icon className="w-4 h-4 text-white" />
                {f.text}
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-white/40">© {new Date().getFullYear()} Cockpit OS — built for multi-entity entrepreneurs.</div>
      </div>

      {/* Right — Google-only auth */}
      <div className="flex flex-col items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6 flex items-center gap-2">
          <button onClick={toggleLang} data-testid="login-lang-toggle"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium text-text-secondary hover:text-text-primary transition-colors">
            <Globe className="w-3.5 h-3.5" /> {lang.toUpperCase()}
          </button>
          <button onClick={toggleTheme} data-testid="login-theme-toggle"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full border text-text-secondary hover:text-text-primary transition-colors">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>

        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10 group" data-testid="login-logo-home-mobile">
            <div className="w-9 h-9 rounded-xl bg-text-primary text-background flex items-center justify-center font-heading font-black transition-transform group-hover:scale-105">
              CO
            </div>
            <span className="font-heading font-bold">{t("app_name")}</span>
          </Link>

          <h2 className="text-2xl font-heading font-medium mb-1" data-testid="auth-title">
            {t("auth_signin_title")}
          </h2>
          <p className="text-text-secondary text-sm mb-8">
            {tr("Connectez-vous avec votre compte Google pour continuer.",
                "Sign in with your Google account to continue.")}
          </p>

          {google.enabled ? (
            <Button
              onClick={startGoogle}
              size="lg"
              className="w-full"
              data-testid="google-login-button"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {t("login_google")}
            </Button>
          ) : (
            <div className="rounded-xl border bg-surface p-4 flex items-start gap-3" data-testid="google-not-configured">
              <ShieldCheck className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-text-secondary">
                {tr("La connexion Google est en cours de configuration. Merci de réessayer dans quelques instants.",
                    "Google sign-in is being set up. Please try again in a moment.")}
              </p>
            </div>
          )}

          <p className="text-xs text-text-secondary text-center mt-6">
            {tr("En continuant, vous acceptez nos ", "By continuing, you agree to our ")}
            <Link to="/terms" className="text-text-primary hover:underline" data-testid="login-terms-link">
              {tr("Conditions", "Terms")}
            </Link>
            {tr(" et notre ", " and ")}
            <Link to="/privacy" className="text-text-primary hover:underline" data-testid="login-privacy-link">
              {tr("Politique de confidentialité", "Privacy Policy")}
            </Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
